import { FormControlLabel, Typography, AccordionDetails, AccordionSummary, Accordion, Grid } from '@mui/material';
import React from 'react';
import { WO2Checkbox, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, HidingOptionsRequest, MdaReportTypes, RealisedCapitalGainsAndLossesOption } from '../../store/types';
import { DateRange } from 'src/store';
import { ExpandMore } from '@mui/icons-material';

export interface RealisedCapitalGainsAndLossesProps extends BaseReportComponentProps {
  expandGainsAndLossesRealised: ActionCreatorWithoutPayload<string>;
  setDateRangeRealisedCapitalGainsAndLossesParameter: ActionCreatorWithPayload<DateRange, string>;
  setRealisedCapitalGainsAndLossesSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setRealisedCapitalAndGainsOptionParameter: ActionCreatorWithPayload<HidingOptionsRequest<RealisedCapitalGainsAndLossesOption>, string>;
}

export const RealisedCapitalGainsAndLosses = ({
  parameters,
  createReportSortRequest,
  setRealisedCapitalGainsAndLossesSelectedParameter,
  setDateRangeRealisedCapitalGainsAndLossesParameter,
  setRealisedCapitalAndGainsOptionParameter,
  expandGainsAndLossesRealised,
}: RealisedCapitalGainsAndLossesProps): JSX.Element => {
  const handleClickGainsLossesRealisedSelect = () => {
    setRealisedCapitalGainsAndLossesSelectedParameter(!parameters.selectedReports?.realisedCapitalGainsAndLosses.selected);
    createReportSortRequest(
      !parameters.selectedReports?.realisedCapitalGainsAndLosses.selected,
      MdaReportTypes.RealisedCapitalGainsAndLosses.id,
      MdaReportTypes.RealisedCapitalGainsAndLosses.displayName
    );
  };

  const handleClickReportDateGainsLossesRealised = (dates: DateRange) => {
    setDateRangeRealisedCapitalGainsAndLossesParameter(dates);
  };

  return (
    <Accordion
      onChange={expandGainsAndLossesRealised}
      expanded={parameters.selectedReports?.realisedCapitalGainsAndLosses.expanded || false}
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.realisedCapitalGainsAndLosses.selected}
              onChangeHandler={handleClickGainsLossesRealisedSelect}
            />
          }
          label="Realised Capital Gains and Losses"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            dateRange={{
              dateFrom: parameters?.selectedReports.realisedCapitalGainsAndLosses?.fromDate,
              dateTo: parameters?.selectedReports.realisedCapitalGainsAndLosses?.toDate,
              dateRangeType: parameters?.selectedReports.realisedCapitalGainsAndLosses?.dateRangeType,
            }}
            setDateRange={handleClickReportDateGainsLossesRealised}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container alignItems="center" spacing={2}>
          <Grid container item xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.realisedCapitalGainsAndLosses.includeSummary}
              onChange={(_, checked: boolean) => {
                setRealisedCapitalAndGainsOptionParameter({
                  option: RealisedCapitalGainsAndLossesOption.IncludeSummary,
                  value: checked,
                });
              }}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Include Summary</Typography>
          </Grid>
          <Grid container item xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.realisedCapitalGainsAndLosses.excludeFxGainsLosses}
              onChange={(_, checked: boolean) => {
                setRealisedCapitalAndGainsOptionParameter({
                  option: RealisedCapitalGainsAndLossesOption.ExcludeFxGainsLosses,
                  value: checked,
                });
              }}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Exclude FX Gains/Losses</Typography>
          </Grid>
          <Grid container item xs={4} alignItems="center">
            <WO2ToggleSwitch
              checked={parameters.selectedReports.realisedCapitalGainsAndLosses.showCostBaseAdjustments}
              onChange={(_, checked: boolean) => {
                setRealisedCapitalAndGainsOptionParameter({
                  option: RealisedCapitalGainsAndLossesOption.ShowCostBaseAdjustments,
                  value: checked,
                });
              }}
              color="primary"
              name="hidingOptions"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4">Show Cost Base Adjustments</Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
